import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'

export default function OrgCreate({ onCreate, defaultParentOrgId = null }) {
    const { show } = useToast()
    const { user } = useAuth()
    const [form, setForm] = useState({
        name: '',
        type: 'GA',
        parentId: ''
    })
    const [parentOptions, setParentOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [parentError, setParentError] = useState(null)

    const isOperator = useMemo(() => Array.isArray(user?.roles) && user.roles.includes(Roles.OPERATOR), [user?.roles])

    // 부모 조직 옵션 로딩
    useEffect(() => {
        let ignore = false
        async function loadParents() {
            try {
                setParentError(null)
                // 운영자는 전체 조직, 그 외에는 내 조직 + 하위 조직만
                const scope = isOperator ? 'all' : 'mine'
                const res = await api.get('/orgs/options', { params: { scope } })
                if (!ignore) {
                    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                    const opts = Array.isArray(payload) ? payload : []
                    setParentOptions(opts)
                }
            } catch (e) {
                if (!ignore) setParentError('상위 조직 옵션을 불러오지 못했습니다')
            }
        }
        loadParents()
        return () => { ignore = true }
    }, [user, isOperator])

    // 기본 상위 조직: (선택된 조직이 있으면) 선택된 조직, 아니면 비운영자는 내 조직
    useEffect(() => {
        // selected org 하위 생성 UX: selected 변경 시 parentId를 따라가도록 key로 컴포넌트를 리마운트시키지만,
        // 방어적으로 여기서도 한 번 더 세팅한다.
        if (defaultParentOrgId != null) {
            setForm(prev => ({ ...prev, parentId: String(defaultParentOrgId) }))
            return
        }
        if (!isOperator && user?.orgId) {
            setForm(prev => (prev.parentId ? prev : ({ ...prev, parentId: String(user.orgId) })))
        }
    }, [defaultParentOrgId, isOperator, user?.orgId])

    // 비운영자는 "상위 조직 타입과 동일" 규칙을 지켜야 하므로, parentId가 바뀌면 type을 parent.type으로 자동 동기화
    useEffect(() => {
        if (isOperator) return
        if (!form.parentId) return
        const parent = parentOptions.find(o => String(o.id) === String(form.parentId))
        if (parent?.type && String(parent.type) !== String(form.type)) {
            setForm(prev => ({ ...prev, type: String(parent.type) }))
        }
    }, [isOperator, form.parentId, form.type, parentOptions])

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)

        try {
            const payload = {
                name: form.name,
                type: form.type,
                parentOrgId: form.parentId ? Number(form.parentId) : undefined
            }
            await api.post('/orgs', payload)
            show('조직 생성 완료', 'success')

            // 폼 초기화
            setForm({ name: '', type: 'GA', parentId: '' })
            onCreate?.()
        } catch (e) {
            show('조직 생성 실패', 'error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-3">
            <div className="font-medium">새 조직 생성</div>

            <input
                className="w-full rounded border px-3 py-2"
                placeholder="조직명"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                required
            />

            <select
                className="w-full rounded border px-3 py-2"
                value={form.type}
                onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                disabled={!isOperator}
            >
                <option value="GA">GA</option>
                <option value="INSURER">INSURER (보험사)</option>
            </select>

            <select
                className="w-full rounded border px-3 py-2"
                value={form.parentId}
                onChange={(e) => setForm(prev => ({ ...prev, parentId: e.target.value }))}
            >
                <option value="">상위 조직 선택</option>
                {parentOptions.map(option => (
                    <option key={option.id} value={option.id}>
                        {option.name} ({option.type})
                    </option>
                ))}
            </select>
            {parentError && (
                <div className="text-xs text-red-600">{parentError}</div>
            )}

            <button
                type="submit"
                className="w-full rounded bg-brand-navy text-white px-3 py-2"
                disabled={loading}
            >
                {loading ? '생성 중...' : '생성'}
            </button>
        </form>
    )
}
